import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, AppstoreOutlined, KeyOutlined, ThunderboltOutlined,
  ArrowLeftOutlined, SettingOutlined, MonitorOutlined,
  SafetyOutlined, LinkOutlined, GlobalOutlined, BulbOutlined,
} from '@ant-design/icons';

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <GlobalOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'instances', label: 'Tipos de Instancia', icon: <AppstoreOutlined /> },
  { id: 'ports', label: 'Puertos y Protocolos', icon: <SettingOutlined /> },
  { id: 'security', label: 'Seguridad y Acceso', icon: <SafetyOutlined /> },
  { id: 'connection', label: 'Conexión', icon: <LinkOutlined /> },
  { id: 'management', label: 'Management UI', icon: <GlobalOutlined /> },
  { id: 'statuses', label: 'Estados del Servicio', icon: <ThunderboltOutlined /> },
  { id: 'events', label: 'Eventos', icon: <MonitorOutlined /> },
  { id: 'concepts', label: 'Conceptos RabbitMQ', icon: <BulbOutlined /> },
];

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">{language}</div>}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed"><code>{code}</code></pre>
      <button onClick={handleCopy} className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100" title="Copiar">
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

const DocsRabbitMQ: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity });
      setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setMobileMenuOpen(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" /></div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">RabbitMQ</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"><ArrowLeftOutlined /> Volver al inicio</Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-gray-600 dark:text-gray-400">{mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          <aside className={`${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8`}>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => scrollTo(s.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeSection === s.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}>
                  <span className="text-base">{s.icon}</span>{s.label}
                </button>
              ))}
            </nav>
          </aside>

          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* INTRO */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full text-xs font-semibold">Documentación</span>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-3 mb-4">RabbitMQ</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy RabbitMQ te permite desplegar un broker de mensajería en infraestructura dedicada en AWS.
                  Configura instancia, almacenamiento, credenciales y acceso. Kubiy se encarga del aprovisionamiento, networking y SSL.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <SettingOutlined className="text-xl text-orange-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Infra Dedicada</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">EC2 exclusiva con RabbitMQ preinstalado</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <GlobalOutlined className="text-xl text-orange-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Management UI</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Interfaz web HTTPS con SSL automático</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <KeyOutlined className="text-xl text-orange-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Credenciales Seguras</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Password en AWS Secrets Manager</p>
                  </div>
                </div>
              </section>

              {/* INICIO RÁPIDO */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <div className="space-y-6">
                  {[
                    { n: 1, title: 'Infraestructura', desc: 'Nombre del servicio, tipo de instancia, almacenamiento y región.' },
                    { n: 2, title: 'Seguridad y acceso', desc: 'Usuario admin (password autogenerado), Management UI y acceso público/VPC.' },
                    { n: 3, title: 'Revisa y crea', desc: 'Confirma configuración y costo. Después del pago, Kubiy provisiona en ~10 minutos.' },
                  ].map((s) => (
                    <div key={s.n} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full flex items-center justify-center text-sm font-bold">{s.n}</div>
                      <div className="flex-1"><h3 className="font-semibold text-gray-900 dark:text-white mb-2">{s.title}</h3><p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p></div>
                    </div>
                  ))}
                </div>
              </section>

              {/* TIPOS DE INSTANCIA */}
              <section id="instances" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tipos de Instancia</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">vCPU</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">RAM</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Caso de uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['t3.small', '2', '2 GB', 'Dev / testing'],
                        ['t3.medium', '2', '4 GB', 'Producción pequeña (recomendado)'],
                        ['t3.large', '2', '8 GB', 'Producción mediana'],
                        ['m5.large', '2', '8 GB', 'Alto rendimiento'],
                        ['m5.xlarge', '4', '16 GB', 'Cargas empresariales'],
                      ].map(([t, cpu, ram, use]) => (
                        <tr key={t}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{t}</code></td><td className="py-3 px-4">{cpu}</td><td className="py-3 px-4">{ram}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{use}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Almacenamiento disponible: 20, 50, 100, 200 o 500 GB (EBS gp3).</p>
              </section>

              {/* PUERTOS */}
              <section id="ports" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Puertos y Protocolos</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Puerto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Protocolo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['5672', 'AMQP', 'Puerto principal de mensajería'],
                        ['15672', 'HTTP/HTTPS', 'Management UI (si está habilitada)'],
                        ['4369', 'EPMD', 'Erlang Port Mapper Daemon'],
                        ['25672', 'Erlang', 'Comunicación inter-nodo'],
                      ].map(([port, proto, desc]) => (
                        <tr key={port}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{port}</code></td><td className="py-3 px-4">{proto}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300"><strong>Nota:</strong> El puerto 15672 se expone via HTTPS con SSL automático a través del ingress de Kubiy.</p>
                </div>
              </section>

              {/* SEGURIDAD */}
              <section id="security" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Seguridad y Acceso</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Credenciales</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  El usuario admin se define al crear el servicio. La contraseña se genera automáticamente y se almacena en AWS Secrets Manager.
                  Se recupera desde el dashboard haciendo clic en <strong>Credentials</strong>.
                </p>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-6">
                  <p className="text-sm text-amber-800 dark:text-amber-300"><strong>Importante:</strong> Crea usuarios adicionales con permisos limitados para las aplicaciones.</p>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acceso Público vs VPC</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Público (Internet)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Puertos accesibles desde internet. Management UI via HTTPS. AMQP via IP/DNS.</p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Privado (Solo VPC)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Solo accesible desde recursos en la misma VPC del tenant. Sin exposición a internet.</p>
                  </div>
                </div>
              </section>

              {/* CONEXIÓN */}
              <section id="connection" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Conexión</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Endpoint</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr><td className="py-3 px-4 font-medium">AMQP Endpoint</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">DNS de Kubiy para conexión AMQP (puerto 5672)</td></tr>
                      <tr><td className="py-3 px-4 font-medium">Management URL</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">HTTPS para la interfaz web de administración</td></tr>
                      <tr><td className="py-3 px-4 font-medium">Public IP</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">IP pública de la EC2 (se recomienda usar DNS)</td></tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Ejemplos</h3>
                <CodeBlock language="text" code="# Connection string AMQP\namqp://usuario:password@TU_DNS_KUBIY:5672" />
                <CodeBlock language="javascript" code={`// Node.js (amqplib)\nconst amqp = require('amqplib');\nconst conn = await amqp.connect('amqp://usuario:password@TU_DNS_KUBIY:5672');`} />
                <CodeBlock language="python" code={`# Python (pika)\nimport pika\ncredentials = pika.PlainCredentials('usuario', 'password')\nparams = pika.ConnectionParameters('TU_DNS_KUBIY', 5672, '/', credentials)\nconnection = pika.BlockingConnection(params)`} />
              </section>

              {/* MANAGEMENT UI */}
              <section id="management" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Management UI</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Interfaz web de administración de RabbitMQ. Si está habilitada, se accede via la Management URL (HTTPS) en el detalle del servicio.
                </p>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Funcionalidades</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Gestionar colas: crear, eliminar, purgar mensajes</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Gestionar exchanges y bindings</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Monitorear conexiones y canales activos</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Administrar usuarios y virtual hosts</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Métricas de rendimiento en tiempo real</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Publicar/consumir mensajes de prueba</li>
                </ul>
              </section>

              {/* ESTADOS */}
              <section id="statuses" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estados del Servicio</h2>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Estado</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Indicador</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['initializing', 'bg-yellow-400 animate-pulse', 'Provisiona EC2, instala RabbitMQ, configura networking y SSL (~10 min)'],
                        ['deploying', 'bg-blue-400 animate-pulse', 'CloudFormation configurando infraestructura'],
                        ['running', 'bg-green-500', 'Activo, aceptando conexiones AMQP y Management UI disponible'],
                        ['stopped', 'bg-gray-400', 'Detenido. Datos conservados.'],
                        ['error', 'bg-red-500', 'Error en creación o ejecución'],
                        ['deleting', 'bg-red-400 animate-pulse', 'Eliminando todos los recursos'],
                      ].map(([st, color, desc]) => (
                        <tr key={st}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{st}</code></td><td className="py-3 px-4"><span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}></span></td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acciones</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Acción</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Disponible en</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Credentials', 'running', 'Recupera usuario/password desde Secrets Manager'],
                        ['Management UI', 'running', 'Abre la interfaz web de administración'],
                        ['Refresh', 'Todos', 'Actualiza información del servicio'],
                        ['Delete', 'Todos (excepto deleting)', 'Elimina permanentemente'],
                      ].map(([a, avail, d]) => (
                        <tr key={a}><td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{a}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{avail}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* EVENTOS */}
              <section id="events" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Eventos</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { type: 'info', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', desc: 'Progreso de operaciones' },
                    { type: 'success', color: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300', desc: 'Operación completada' },
                    { type: 'warning', color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', desc: 'Requiere atención' },
                    { type: 'error', color: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300', desc: 'Error en operación' },
                  ].map((e) => (
                    <div key={e.type} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.color}`}>{e.type}</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{e.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* CONCEPTOS */}
              <section id="concepts" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Conceptos Básicos de RabbitMQ</h2>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Concepto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Producer', 'Aplicación que envía mensajes al broker'],
                        ['Consumer', 'Aplicación que recibe y procesa mensajes'],
                        ['Queue', 'Cola donde se almacenan mensajes hasta ser procesados'],
                        ['Exchange', 'Punto de entrada que enruta mensajes a colas según reglas'],
                        ['Binding', 'Regla que conecta un exchange con una cola'],
                        ['Virtual Host', 'Espacio de nombres aislado dentro de RabbitMQ'],
                        ['Channel', 'Canal de comunicación dentro de una conexión'],
                        ['AMQP', 'Advanced Message Queuing Protocol — protocolo estándar'],
                      ].map(([c, d]) => (
                        <tr key={c}><td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{c}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tipos de Exchange</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Enrutamiento</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['direct', 'Routing key exacto → cola específica'],
                        ['fanout', 'Broadcast → todas las colas vinculadas'],
                        ['topic', 'Patrón con * y # → colas que coincidan'],
                        ['headers', 'Basado en headers del mensaje'],
                      ].map(([t, r]) => (
                        <tr key={t}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{t}</code></td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{r}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>

      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">&copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.</span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span><a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span><a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsRabbitMQ;